import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import ReloadOutlined from "@ant-design/icons/ReloadOutlined";
import EyeOutlined from "@ant-design/icons/EyeOutlined";
import EyeInvisibleOutlined from "@ant-design/icons/EyeInvisibleOutlined";
import {
  listMemorialEntriesAdmin,
  withdrawMemorialEntry,
  type MemorialEntryAdminRow,
} from "./memorialAdminApi";
import type { ColumnsType } from "antd/es/table";
import { PageHeading } from "../../../components/PageHeading";
import { LoadingSkeleton } from "../../../components/LoadingSkeleton";
import { ErrorState } from "../../../components/ErrorState";
import { EmptyState } from "../../../components/EmptyState";
import { useActorContext } from "../../../auth/ActorContext";
import type { CurrentUser } from "../../../api/currentUser";
import { searchCemeteries } from "../cemeteryApi";
import "../cemeteries.css";

const STATUS_OPTIONS = [
  { value: "PUBLISHED", label: "Đã đăng" },
  { value: "REJECTED", label: "Từ chối" },
  { value: "HELD", label: "Đang giữ" },
  { value: "WITHDRAWN_BY_POLICY", label: "Tự động gỡ" },
  { value: "WITHDRAWN_MANUAL", label: "Đã gỡ tay" },
] as const;

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string }
> = {
  PUBLISHED: { label: "Đã đăng", color: "success" },
  REJECTED: { label: "Từ chối", color: "error" },
  HELD: { label: "Đang giữ", color: "warning" },
  WITHDRAWN_BY_POLICY: { label: "Tự động gỡ", color: "purple" },
  WITHDRAWN_MANUAL: { label: "Đã gỡ tay", color: "default" },
};
function getMemorialCemeteryScopeIds(actor: CurrentUser): string[] {
  const ids = new Set<string>();
  for (const grant of actor.grants ?? []) {
    if (grant.actionCodes?.includes("MEMORIAL_ENTRY_READ")) {
      for (const ds of grant.authorizationScope?.dataScopes ?? []) {
        if (ds.type === "CEMETERY" && ds.id) {
          ids.add(ds.id);
        }
      }
    }
  }
  return Array.from(ids).sort();
}

async function loadCemeteryOptions(
  scopeIds: readonly string[],
  signal: AbortSignal,
): Promise<ReadonlyArray<{ value: string; label: string }>> {
  if (scopeIds.length === 0) return [];
  const targetIds = new Set(scopeIds);
  const optionsMap = new Map<string, string>();
  let page = 0;

  while (optionsMap.size < targetIds.size) {
    const res = await searchCemeteries({ page, size: 100 }, signal);
    for (const row of res.rows ?? []) {
      if (targetIds.has(row.cemeteryId)) {
        const name = row.name?.trim();
        const code = row.cemeteryCode?.trim();
        const label = name || code;
        if (!label) {
          throw new Error(`Nghĩa trang ${row.cemeteryId} không có tên hoặc mã`);
        }
        optionsMap.set(row.cemeteryId, label);
      }
    }
    const totalPages = res.meta?.totalPages;
    if (optionsMap.size < targetIds.size) {
      if (totalPages !== undefined && totalPages !== null) {
        if (page + 1 >= totalPages) {
          throw new Error("Không thể ánh xạ đủ phạm vi nghĩa trang");
        }
      } else if (!res.rows || res.rows.length === 0 || res.rows.length < 100) {
        throw new Error("Không thể ánh xạ đủ phạm vi nghĩa trang");
      }
    }
    page += 1;
  }

  return scopeIds.map((id) => ({
    value: id,
    label: optionsMap.get(id)!,
  }));
}

type OptionsState =
  | { status: "loading" }
  | { status: "ready"; options: ReadonlyArray<{ value: string; label: string }> }
  | { status: "error" };


type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: MemorialEntryAdminRow[] }
  | { status: "error" };

export function MemorialEntriesAdminPage(): ReactNode {
  const actorState = useActorContext();
  const actor: CurrentUser | null =
    actorState.status === "ready" ||
    actorState.status === "refreshing" ||
    actorState.status === "stale"
      ? actorState.actor
      : null;

  const cemeteryScopeIds = useMemo(
    () => (actor ? getMemorialCemeteryScopeIds(actor) : []),
    [actor],
  );

  const [cemeteryIdFilter, setCemeteryIdFilter] = useState<string | undefined>(() =>
    cemeteryScopeIds.length === 1 ? cemeteryScopeIds[0] : undefined,
  );
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [optionsState, setOptionsState] = useState<OptionsState>({ status: "loading" });
  const [state, setState] = useState<ListState>({ status: "loading" });

  // Modal gỡ bài
  const [withdrawingEntry, setWithdrawingEntry] = useState<MemorialEntryAdminRow | null>(null);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Tập ID các dòng có nội dung nhạy cảm được bấm mở xem
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());

  const toggleReveal = (id: string) => {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  useEffect(() => {
    if (cemeteryScopeIds.length === 1) {
      setCemeteryIdFilter(cemeteryScopeIds[0]);
    } else if (cemeteryScopeIds.length > 1) {
      setCemeteryIdFilter((prev) => (prev && cemeteryScopeIds.includes(prev) ? prev : undefined));
    } else {
      setCemeteryIdFilter(undefined);
    }
  }, [cemeteryScopeIds]);

  useEffect(() => {
    if (cemeteryScopeIds.length === 0) {
      setOptionsState({ status: "ready", options: [] });
      return;
    }
    const controller = new AbortController();
    setOptionsState({ status: "loading" });
    loadCemeteryOptions(cemeteryScopeIds, controller.signal)
      .then((options) => {
        setOptionsState({ status: "ready", options });
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (controller.signal.aborted) return;
        setOptionsState({ status: "error" });
      });
    return () => {
      controller.abort();
    };
  }, [cemeteryScopeIds, refreshNonce]);

  useEffect(() => {
    const controller = new AbortController();
    setState((prev) => (prev.status === "ready" ? prev : { status: "loading" }));

    listMemorialEntriesAdmin(
      {
        ...(cemeteryIdFilter ? { cemeteryId: cemeteryIdFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      },
      controller.signal,
    )
      .then((res) => {
        setState({ status: "ready", rows: res.rows });
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (controller.signal.aborted) return;
        setState({ status: "error" });
      });

    return () => {
      controller.abort();
    };
  }, [cemeteryIdFilter, statusFilter, refreshNonce]);

  const handleOpenWithdraw = (row: MemorialEntryAdminRow) => {
    setWithdrawingEntry(row);
    setWithdrawReason("");
    setActionSuccess(null);
    setActionError(null);
  };

  const handleConfirmWithdraw = async () => {
    if (!withdrawingEntry || !withdrawReason.trim()) {
      message.warning("Vui lòng nhập lý do gỡ bài.");
      return;
    }

    setSubmittingWithdraw(true);
    try {
      const res = await withdrawMemorialEntry(withdrawingEntry.id, withdrawReason.trim());
      setActionSuccess("Đã gỡ lời tri ân thành công.");
      message.success("Đã gỡ lời tri ân thành công.");
      // Chuyển trạng thái tại chỗ
      setState((prev) =>
        prev.status === "ready"
          ? {
              ...prev,
              rows: prev.rows.map((item) =>
                item.id === withdrawingEntry.id
                  ? {
                      ...item,
                      status: "WITHDRAWN_MANUAL",
                      withdrawnAt: res.withdrawnAt,
                      withdrawalReason: withdrawReason.trim(),
                    }
                  : item
              ),
            }
          : prev
      );
      setWithdrawingEntry(null);
    } catch (err: unknown) {
      const anyErr = err as { status?: number; problem?: { code?: string }; message?: string };
      if (
        anyErr?.status === 409 ||
        anyErr?.problem?.code === "MEMORIAL_ENTRY_NOT_WITHDRAWABLE"
      ) {
        const notWithdrawableMsg = "Lời tri ân đã bị gỡ hoặc chưa từng được đăng công khai.";
        setActionError(notWithdrawableMsg);
        message.error(notWithdrawableMsg);
        return;
      }
      const msg =
        typeof anyErr?.message === "string" && anyErr.message
          ? anyErr.message
          : "Không thể thực hiện gỡ bài.";
      setActionError(msg);
      message.error(msg);
    } finally {
      setSubmittingWithdraw(false);
    }
  };

  const columns: ColumnsType<MemorialEntryAdminRow> = [
    {
      title: "Mã mộ",
      dataIndex: "graveCode",
      key: "graveCode",
      width: 120,
    },
    {
      title: "Nội dung tri ân",
      dataIndex: "contentText",
      key: "contentText",
      render: (text: string, record) => {
        const isRejected = record.status === "REJECTED";
        const isRevealed = revealedIds.has(record.id);

        return (
          <div>
            {isRejected && !isRevealed ? (
              <Space size={6} align="center" style={{ marginBottom: 4 }}>
                <Typography.Text
                  type="secondary"
                  style={{
                    fontStyle: "italic",
                    background: "#f5f5f5",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                >
                  [Nội dung nhạy cảm đã bị ẩn]
                </Typography.Text>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined style={{ color: "#8c8c8c" }} />}
                  onClick={() => toggleReveal(record.id)}
                  title="Xem nội dung nhạy cảm"
                  aria-label="Xem nội dung nhạy cảm"
                />
              </Space>
            ) : (
              <Space size={6} align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                <Typography.Paragraph
                  ellipsis={{ rows: 2, expandable: true, symbol: "Xem thêm" }}
                  style={{ marginBottom: 4, fontStyle: "italic" }}
                >
                  &ldquo;{text}&rdquo;
                </Typography.Paragraph>
                {isRejected && (
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeInvisibleOutlined style={{ color: "#8c8c8c" }} />}
                    onClick={() => toggleReveal(record.id)}
                    title="Ẩn nội dung nhạy cảm"
                    aria-label="Ẩn nội dung nhạy cảm"
                  />
                )}
              </Space>
            )}
            {record.withdrawalReason && (
              <Typography.Text type="secondary" className="s11-cell-sub">
                Lý do gỡ: {record.withdrawalReason}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Người gửi",
      key: "author",
      width: 160,
      render: (_, record) => (
        <div className="s11-cell-stack">
          <div>{record.penName || "Khách viếng"}</div>
          {record.relationDisplay && (
            <Typography.Text type="secondary" className="s11-cell-sub">
              {record.relationDisplay}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status: string) => {
        const conf = STATUS_CONFIG[status] || { label: status, color: "default" };
        return <Tag color={conf.color}>{conf.label}</Tag>;
      },
    },
    {
      title: "Phiên bản từ",
      dataIndex: "wordVersion",
      key: "wordVersion",
      width: 110,
      render: (v: number | null) => (v !== null ? `V${v}` : "—"),
    },
    {
      title: "Thời gian gửi",
      dataIndex: "submittedAt",
      key: "submittedAt",
      width: 160,
      render: (t: string) => (t ? new Date(t).toLocaleString("vi-VN") : "—"),
    },
    {
      title: "Hành động",
      key: "actions",
      width: 100,
      render: (_, record) => {
        if (record.status === "PUBLISHED") {
          return (
            <Button
              danger
              size="small"
              onClick={() => handleOpenWithdraw(record)}
            >
              Gỡ bài
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const rows = state.status === "ready" ? state.rows : [];

  return (
    <div className="s11-list-frame">
      <PageHeading
        title="Quản lý Sổ tang điện tử"
        description="Duyệt và quản lý lời tri ân gửi tới các liệt sĩ trong phạm vi nghĩa trang được cấp."
      />

      {state.status === "ready" ? (
        <Typography.Text type="secondary" className="s11-list-stats" role="status">
          {`${rows.length} lời tri ân`}
        </Typography.Text>
      ) : null}

      <section className="card s11-filters" role="search" aria-label="Bộ lọc lời tri ân">
        <div className="s11-filter-toolbar">
          <div className="s11-filter-toolbar-slot">
            <Select
              aria-label="Nghĩa trang"
              placeholder={
                optionsState.status === "error"
                  ? "Không tải được danh sách nghĩa trang"
                  : "Tất cả nghĩa trang"
              }
              popupMatchSelectWidth={false}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              loading={optionsState.status === "loading"}
              disabled={optionsState.status !== "ready"}
              {...(optionsState.status === "error" ? { status: "error" as const } : {})}
              allowClear={cemeteryScopeIds.length > 1}
              value={optionsState.status === "ready" ? cemeteryIdFilter : undefined}
              onChange={(val) =>
                setCemeteryIdFilter(
                  val ?? (cemeteryScopeIds.length === 1 ? cemeteryScopeIds[0] : undefined),
                )
              }
              options={
                optionsState.status === "ready"
                  ? (optionsState.options as { value: string; label: string }[])
                  : []
              }
            />
          </div>
          <div className="s11-filter-toolbar-slot">
            <Select
              placeholder="Tất cả trạng thái"
              aria-label="Trạng thái"
              popupMatchSelectWidth={false}
              allowClear
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              options={[...STATUS_OPTIONS]}
            />
          </div>
          <Space className="s11-filter-toolbar-actions">
            <Button
              icon={<ReloadOutlined />}
              aria-label="Làm mới"
              onClick={() => setRefreshNonce((n) => n + 1)}
            />
          </Space>
        </div>
      </section>

      {actionSuccess && (
        <Alert
          message={actionSuccess}
          type="success"
          showIcon
          closable
          role="alert"
          onClose={() => setActionSuccess(null)}
          style={{ margin: "var(--ncc-spacing-sm, 8px) 1.25rem 0" }}
        />
      )}
      {actionError && (
        <Alert
          message={actionError}
          type="error"
          showIcon
          closable
          role="alert"
          onClose={() => setActionError(null)}
          style={{ margin: "var(--ncc-spacing-sm, 8px) 1.25rem 0" }}
        />
      )}

      {state.status === "loading" ? (
        <div role="status" aria-label="Đang tải danh sách lời tri ân" className="s11-list-zone">
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}

      {state.status === "error" ? (
        <div className="s11-list-zone">
          <ErrorState
            title="Không thể tải danh sách lời tri ân"
            description="Kiểm tra bộ lọc hoặc thử lại sau ít phút."
            onRetry={() => setRefreshNonce((n) => n + 1)}
          />
        </div>
      ) : null}

      {state.status === "ready" ? (
        rows.length === 0 ? (
          <div className="s11-list-zone">
            <EmptyState
              title="Chưa có lời tri ân nào"
              description="Chưa có lời tri ân nào phù hợp với bộ lọc hiện tại."
              action={
                statusFilter !== undefined ||
                (cemeteryScopeIds.length > 1 && cemeteryIdFilter !== undefined) ? (
                  <Button
                    onClick={() => {
                      setStatusFilter(undefined);
                      setCemeteryIdFilter(
                        cemeteryScopeIds.length === 1 ? cemeteryScopeIds[0] : undefined,
                      );
                    }}
                  >
                    Xóa bộ lọc
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <div className="s11-table-card">
            <Table<MemorialEntryAdminRow>
              rowKey="id"
              columns={columns}
              dataSource={rows}
              size="small"
              sticky
              pagination={{ pageSize: 15, showSizeChanger: false,
                showTotal: (total, range) => `Hiển thị ${range[0]}–${range[1]} / ${total}`,
              }}
            />
          </div>
        )
      ) : null}

      <Modal
        title="Xác nhận gỡ bài tri ân"
        open={withdrawingEntry !== null}
        onCancel={() => setWithdrawingEntry(null)}
        onOk={() => {
          void handleConfirmWithdraw();
        }}
        confirmLoading={submittingWithdraw}
        okText="Xác nhận gỡ"
        cancelText="Hủy"
        okButtonProps={{ danger: true, disabled: !withdrawReason.trim() }}
      >
        <p>
          Bạn đang gỡ lời tri ân khỏi không gian tưởng niệm công khai. Hành động này sẽ được ghi nhận
          vào nhật ký hệ thống và không thể hoàn tác.
        </p>
        <Form layout="vertical">
          <Form.Item label="Lý do gỡ bài (bắt buộc)" required>
            <Input.TextArea
              rows={4}
              maxLength={500}
              showCount
              placeholder="Nhập lý do gỡ bài giải trình theo khiếu nại (1–500 ký tự)..."
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
